import truncate from 'lodash/truncate';
import { Box, Button, ScrollView, VStack } from 'native-base';
import React, { ReactNode, useMemo, useState } from 'react';

import Accordion from 'app/components/Accordion';
import CPModal from 'app/components/CPModal';
import HTML from 'app/components/HTML';
import { AttractionContent } from 'app/hooks/useAttraction';
import useGuest from 'app/hooks/useGuest';
import ReservationBarcodeItem from 'app/screens/AttractionScreen/ReservationRedirectModal/ReservationBarcodeItem';
import Analytics, { Events } from 'app/services/Analytics';
import { ReservationTextHelper, TranslationHelper } from 'app/services/I18n';

const MAX_HEIGHT_TICKETS = 190;
const MAX_CONTINUE_LENGTH = 34;

export type ReservationRedirectModalProps = {
  children?: ReactNode;
  t: TranslationHelper;
  rt: ReservationTextHelper;
  isOpen: boolean;
  attractionContent: AttractionContent;
  productContentKey: string;
  attractionContentKey: string;
  onClose: () => void;
  onContinue: () => void;
};

export default function ReservationRedirectModalPartial({
  t,
  rt,
  isOpen,
  onClose,
  onContinue,
  attractionContent,
  productContentKey,
  attractionContentKey,
}: ReservationRedirectModalProps) {
  const [openSectionIdx, setOpenSectionIdx] = useState<number | null>(0);
  const eventData = {
    productKey: productContentKey,
    attractionKey: attractionContentKey,
  };

  const { activeEntitlement } = useGuest();

  const passes = activeEntitlement?.passes ?? [];

  useMemo(() => {
    if (!isOpen) {
      setOpenSectionIdx(0);
    }
  }, [isOpen]);

  const handleContinue = () => {
    onContinue();
    Analytics.trackEvent(Events.ReservationRedirectContinue, eventData);
  };
  const handleClose = () => {
    onClose();
    Analytics.trackEvent(Events.ReservationRedirectCancel, eventData);
  };

  return (
    <CPModal isOpen={isOpen} size="lg" onClose={handleClose}>
      <CPModal.Content>
        <CPModal.CloseButton />
        <CPModal.Header>{t('app_leaving_app')}</CPModal.Header>
        <CPModal.Body>
          <HTML html={rt('redirect_details_html')} />
          <Accordion
            sections={[
              {
                id: 'reservation-barcodes',
                title: t('app_leaving_travel_party'),
                _subtitle: { color: 'gray.400', fontSize: 'xs' },
                subtitle:
                  openSectionIdx === 0 &&
                  rt('app_leaving_travel_party_subtitle', {
                    attraction_name: attractionContent?.name,
                  }),
                initialOpen: true,
                content: (
                  <ScrollView maxHeight={MAX_HEIGHT_TICKETS} nestedScrollEnabled>
                    <VStack space={2}>
                      {passes?.map((pass) => (
                        <ReservationBarcodeItem key={pass.barcode} pass={pass} />
                      ))}
                    </VStack>
                  </ScrollView>
                ),
              },
              {
                id: 'reservation-changes',
                title: t('app_leaving_reservation_change'),
                initialOpen: false,
                content: (
                  <Box>
                    {!!rt('changes', { fallback: false }) && (
                      <HTML html={`${rt('changes_title')}: ${rt('changes')}`} />
                    )}
                  </Box>
                ),
              },
            ].map((section, idx) => ({
              ...section,
              open: openSectionIdx === idx,
              onToggle: (open) => setOpenSectionIdx(open ? idx : null),
            }))}
          />
        </CPModal.Body>
        <CPModal.Footer>
          <Button w="full" onPress={handleContinue}>
            {truncate(rt('app_leaving_continue', { attraction_name: attractionContent?.name }), {
              length: MAX_CONTINUE_LENGTH,
            })}
          </Button>
        </CPModal.Footer>
      </CPModal.Content>
    </CPModal>
  );
}
